import React, { useState, useEffect, useContext } from "react";
import "./AccountMaintenance.css";
import { Link } from "react-router-dom";
import {
  INVITE_SENT_SUCCESS_MESSAGE,
  PAGINATION_PAGE_SIZE,
} from "../../utils/constants";
import useFBOApi from "../../services/api/useFBOApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import ROUTE_PATHS from "../../utils/routePaths";
import { CaretLeftOutlined, CaretRightOutlined } from "@ant-design/icons";
import AccountUpdateTable from "./Tables/AccountUpdateTable";
import DeleteAccountDialog from "../../components/element/DeleteAccountDialog";
import InviteUserDialog from "../../components/element/InviteUserDialog";
import EditAccountDialog from "../../components/element/EditAccountDialog";
import SnackbarAlert from "../../components/element/SnackbarAlert";
import { Button } from "@mui/material";
import FBOHeaderBar from "../../components/element/FBOHeaderBar";
import NavigationBar from "../../components/element/NavigationBar";
import { StorageContext } from "../../services/StorageProvider";

const AccountMaintenance = () => {
  const { deleteFBOProfile, editFBOProfile, fetchFBOProfiles, inviteFBOUser } =
    useFBOApi();
  const { fboUserProfileData } = useContext(StorageContext);
  const [currentPage, setCurrentPage] = useState(() => {
    return sessionStorage.getItem("accountMaintenanceCurrentPage")
      ? Number(sessionStorage.getItem("accountMaintenanceCurrentPage"))
      : 0;
  });
  const pageSize = PAGINATION_PAGE_SIZE;
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [showInviteUserDialog, setShowInviteUserDialog] = useState(false);
  const [showEditAccountDialog, setShowEditAccountDialog] = useState(false);
  const [actionCount, setActionCount] = useState(0);
  const [userFBOId, setUserFBOId] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);

  useEffect(() => {
    if (fboUserProfileData) {
      setUserFBOId(fboUserProfileData?.associations[0]?.fboId);
    }
  }, [fboUserProfileData]);

  useEffect(() => {
    sessionStorage.setItem("accountMaintenanceCurrentPage", currentPage);
  }, [currentPage]);

  const {
    data: accountApprovalData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["get-fbo-profile-list", currentPage, userFBOId, actionCount],
    queryFn: () =>
      fetchFBOProfiles({
        page: currentPage,
        size: pageSize,
        fboId: userFBOId,
        roles: "FBO_ADMIN,FBO_EMPLOYEE",
        status: "APPROVED",
      }),
    enabled: !!userFBOId,
    retry: false,
  });

  const inviteFBOUserMutation = useMutation({
    mutationFn: ({ userData }) => inviteFBOUser(userData),
    onSuccess: () => {
      setShowInviteUserDialog(false);
      setActionCount(actionCount + 1);
    },
  });

  const editFBOProfileMutation = useMutation({
    mutationFn: ({ associationId, data }) =>
      editFBOProfile(associationId, data),
    onSuccess: () => {
      setShowEditAccountDialog(false);
      setActionCount(actionCount + 1);
    },
  });

  const deleteFBOProfileMutation = useMutation({
    mutationFn: ({ associationId }) => deleteFBOProfile(associationId),
    onSuccess: () => {
      setShowDeleteAccountDialog(false);
      setActionCount(actionCount + 1);
    },
  });

  const handleNextPage = () => {
    if (currentPage + 1 < accountApprovalData?.data?.totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleAccountUpdateEdit = (item) => {
    setSelectedProfile(item);
    setShowEditAccountDialog(true);
  };

  const handleAccountUpdateDelete = (item) => {
    setSelectedProfile(item);
    setShowDeleteAccountDialog(true);
  };

  const handleEditAccountSave = (selectedAccountType) => {
    editFBOProfileMutation.mutate({
      associationId: selectedProfile?.associations[0]?.id,
      data: {
        role: selectedAccountType,
      },
    });
  };

  const handleAccountUpdateConfirmDelete = () => {
    deleteFBOProfileMutation.mutate({
      associationId: selectedProfile?.associations[0]?.id,
    });
  };

  const handleInviteFBOUser = (firstName, lastName, email, selectedFBOType) => {
    inviteFBOUserMutation.mutate({
      userData: {
        invitedRoleType: selectedFBOType,
        email: email,
        firstName: firstName,
        lastName: lastName,
      },
    });
  };

  return (
    <div>
      {inviteFBOUserMutation.isSuccess ? (
        <SnackbarAlert type={"success"}>
          {INVITE_SENT_SUCCESS_MESSAGE}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {inviteFBOUserMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {inviteFBOUserMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      <div className="account-maintenance-container">
        <NavigationBar />
        <FBOHeaderBar
          title={"Account Maintenance"}
          backContent={
            <div className="navigation-header">
              <Link
                to={ROUTE_PATHS.FBO_HOME}
                className="navigation-header-link"
              >
                Home/
              </Link>
              Account Maintenance
            </div>
          }
        />
        <div className="account-maintenance-header-container">
          <Button
            variant="contained"
            color="send"
            className="invite"
            onClick={() => setShowInviteUserDialog(true)}
          >
            Invite
          </Button>
        </div>
        <AccountUpdateTable
          accountUpdateData={accountApprovalData?.data}
          indexStartFrom={pageSize * currentPage}
          isLoading={isLoading}
          error={error}
          onClickEdit={handleAccountUpdateEdit}
          onClickDelete={handleAccountUpdateDelete}
        />
      </div>
      {accountApprovalData?.data?.content &&
        accountApprovalData?.data?.content.length > 0 && (
          <div className="pagination-container">
            <div className="pagination-content">
              Page {currentPage + 1} of {accountApprovalData?.data?.totalPages}
            </div>
            <button
              className="pagination-button"
              onClick={handlePreviousPage}
              disabled={currentPage === 0}
            >
              <CaretLeftOutlined />
            </button>
            <button
              className="pagination-button"
              onClick={handleNextPage}
              disabled={
                currentPage + 1 === accountApprovalData?.data?.totalPages
              }
            >
              <CaretRightOutlined />
            </button>
          </div>
        )}
      <InviteUserDialog
        open={showInviteUserDialog}
        onClose={() => setShowInviteUserDialog(false)}
        onClickAdd={handleInviteFBOUser}
        isLoading={inviteFBOUserMutation?.isPending}
      />
      <EditAccountDialog
        open={showEditAccountDialog}
        selectedProfile={selectedProfile}
        onClose={() => setShowEditAccountDialog(false)}
        onClickSave={handleEditAccountSave}
        isLoading={editFBOProfileMutation?.isPending}
      />
      <DeleteAccountDialog
        open={showDeleteAccountDialog}
        username={`${selectedProfile?.firstName} ${selectedProfile?.lastName}`}
        onClose={() => setShowDeleteAccountDialog(false)}
        onClickDelete={handleAccountUpdateConfirmDelete}
        isLoading={deleteFBOProfileMutation?.isPending}
      />
    </div>
  );
};

export default AccountMaintenance;
